import React, { useState } from 'react';
import axios from 'axios';
import {
  Activity, TrendingUp, AlertTriangle, RefreshCw, Warehouse,
  ShieldAlert, CheckCircle, X, Check
} from 'lucide-react';
import { Badge, Th, Td, HelpTip } from '../WMSShared';

const API = process.env.REACT_APP_BACKEND_URL + '/api/wms';

const RISK_COLOR = {
  high:   'bg-red-100 text-red-700 border-red-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  low:    'bg-blue-100 text-blue-700 border-blue-200',
};

const ZONE_COLOR = {
  A: 'bg-emerald-100 text-emerald-700',
  B: 'bg-blue-100 text-blue-700',
  C: 'bg-gray-100 text-gray-600',
};

export default function AITab({
  aiEngine, ollamaModel, chatMessages, chatInput, aiLoading, forecasts, reorderAlerts,
  setChatInput, sendChat, loadForecast, loadReorderAlerts, switchAiEngine, switchOllamaModel
}) {
  // 1. Remember active sub-tab in localStorage
  const [aiSubTab, setAiSubTab] = useState(
    () => localStorage.getItem('wms_ai_subtab') || 'assistant'
  );
  const switchSubTab = (tab) => {
    setAiSubTab(tab);
    localStorage.setItem('wms_ai_subtab', tab);
  };

  // Analytics local state
  const [slottingData, setSlottingData]       = useState(null);
  const [anomalyData, setAnomalyData]         = useState(null);
  const [slottingLoading, setSlottingLoading] = useState(false);
  const [anomalyLoading, setAnomalyLoading]   = useState(false);
  const [slottingError, setSlottingError]     = useState('');
  const [anomalyError, setAnomalyError]       = useState('');
  // Track which slotting rows have been confirmed
  const [confirmedMoves, setConfirmedMoves]   = useState({});

  // 2 & 3. Load cached results on mount
  React.useEffect(() => {
    Promise.all([
      axios.get(`${API}/ai/cache/slotting`).catch(() => null),
      axios.get(`${API}/ai/cache/anomalies`).catch(() => null),
    ]).then(([sRes, aRes]) => {
      if (sRes?.data?.data) setSlottingData(sRes.data.data);
      if (aRes?.data?.data) setAnomalyData(aRes.data.data);
    });
  }, []);

  const saveToCache = async (type, data) => {
    try { await axios.put(`${API}/ai/cache/${type}`, { data }); } catch {}
  };

  const loadSlotting = async () => {
    setSlottingLoading(true);
    setSlottingError('');
    try {
      const res = await axios.get(`${API}/ai/slotting`);
      setSlottingData(res.data);
      saveToCache('slotting', res.data);   // 2. persist
    } catch (e) {
      setSlottingError(e.response?.data?.detail || 'Analysis failed — check Ollama server');
    }
    setSlottingLoading(false);
  };

  const loadAnomalies = async () => {
    setAnomalyLoading(true);
    setAnomalyError('');
    try {
      const res = await axios.get(`${API}/ai/anomalies`);
      setAnomalyData(res.data);
      saveToCache('anomalies', res.data);  // 3. persist
    } catch (e) {
      setAnomalyError(e.response?.data?.detail || 'Scan failed — check Ollama server');
    }
    setAnomalyLoading(false);
  };

  const confirmSlottingMove = async (rec) => {
    const key = rec.product_name;
    setConfirmedMoves(p => ({ ...p, [key]: 'loading' }));
    try {
      await axios.post(`${API}/ai/slotting/execute`, {
        product_name:     rec.product_name,
        recommended_zone: rec.recommended_zone,
        action:           rec.action,
        confirmed_by:     'manager',
      });
      setConfirmedMoves(p => ({ ...p, [key]: 'done' }));
      setTimeout(() => loadSlotting(), 800);
    } catch {
      setConfirmedMoves(p => ({ ...p, [key]: 'error' }));
    }
  };

  return (
    <div>
      {/* Header + Engine selector */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold">AI Intelligence</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Powered by {aiEngine === 'gemini' ? 'Google Gemini' : `Ollama (${ollamaModel})`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Engine:</span>
          <button onClick={() => switchAiEngine('gemini')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${aiEngine === 'gemini' ? 'bg-blue-500 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            Gemini
          </button>
          <button onClick={() => switchAiEngine('ollama')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${aiEngine === 'ollama' ? 'bg-emerald-500 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            Ollama
          </button>
          <select value={ollamaModel} onChange={e => switchOllamaModel(e.target.value)}
            className="text-xs border rounded-lg px-2 py-1.5 bg-white">
            <option value="llama3.2:3b">llama3.2:3b (faster)</option>
            <option value="llama3:8b">llama3:8b</option>
          </select>
        </div>
      </div>

      {/* Sub-tabs — only 2 now */}
      <div className="flex gap-2 mb-6 border-b pb-3">
        {[
          ['assistant', Activity,    'AI Assistant'],
          ['analytics', ShieldAlert, 'Analytics'],
        ].map(([id, Icon, label]) => (
          <button key={id} onClick={() => switchSubTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${aiSubTab === id ? 'bg-blue-500 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            <Icon className="w-4 h-4" />{label}
          </button>
        ))}
      </div>

      {/* ── AI ASSISTANT ── */}
      {aiSubTab === 'assistant' && (
        <div>
          {/* Chat */}
          <div className="bg-white rounded-xl border shadow-sm mb-6">
            <div className="px-5 py-4 border-b bg-gradient-to-r from-purple-50 to-blue-50">
              <h3 className="font-semibold flex items-center gap-1">
                <Activity className="w-4 h-4 text-purple-600" />
                AI Warehouse Assistant
                <HelpTip text="Ask anything in plain English. Examples: 'What's my best selling product?' or 'Which items need restocking?' — the AI reads your live warehouse data and answers." />
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">Ask anything about your inventory, orders, products, or operations</p>
            </div>
            <div className="h-80 overflow-y-auto p-4 space-y-3" id="chat-container">
              {chatMessages.length === 0 && (
                <div className="text-center py-8 text-gray-400">
                  <Activity className="w-10 h-10 mx-auto mb-3 text-purple-300" />
                  <p className="font-medium">Ask me anything!</p>
                  <div className="flex flex-wrap gap-2 justify-center mt-4">
                    {["What's my best selling product?", "Which items need restocking?", "Show profit margins by category", "How's Shopee vs eBay performance?", "Suggest ways to reduce inventory costs"].map(q => (
                      <button key={q} onClick={() => setChatInput(q)}
                        className="text-xs bg-purple-50 text-purple-700 px-3 py-1.5 rounded-full hover:bg-purple-100 transition-colors">{q}</button>
                    ))}
                  </div>
                </div>
              )}
              {chatMessages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${m.role === 'user' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-800'}`}>
                    {m.role === 'ai' ? <div className="whitespace-pre-wrap">{m.text}</div> : m.text}
                  </div>
                </div>
              ))}
              {aiLoading.chat && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 rounded-2xl px-4 py-2.5 text-sm text-gray-500 animate-pulse">Analyzing warehouse data...</div>
                </div>
              )}
              {chatMessages.length > 0 && !aiLoading.chat && chatMessages[chatMessages.length - 1]?.role === 'ai' && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {["Which items need restocking?", "Top 5 customers?", "Predict next month revenue", "Lowest margin products?"]
                    .filter(q => !chatMessages.some(m => m.role === 'user' && m.text === q))
                    .slice(0, 3)
                    .map(q => (
                      <button key={q} onClick={() => setChatInput(q)}
                        className="text-xs bg-purple-50 text-purple-700 px-3 py-1.5 rounded-full hover:bg-purple-100 transition-colors">{q}</button>
                    ))}
                </div>
              )}
            </div>
            <div className="p-4 border-t flex gap-2">
              <input type="text" value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') sendChat(); }}
                placeholder="Ask about inventory, orders, profits..."
                className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400" />
              <button onClick={sendChat} disabled={aiLoading.chat || !chatInput.trim()}
                className="bg-purple-500 text-white px-5 py-2.5 rounded-xl text-sm font-medium hover:bg-purple-600 disabled:bg-gray-300 transition-colors">Send</button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Demand Forecast */}
            <div className="bg-white rounded-xl border shadow-sm">
              <div className="px-5 py-4 border-b flex items-center justify-between bg-gradient-to-r from-blue-50 to-cyan-50">
                <div>
                  <h3 className="font-semibold flex items-center gap-1">
                    <TrendingUp className="w-4 h-4 text-blue-600" />
                    Demand Forecast
                    <HelpTip text="Predicts how fast each product will sell and how many weeks of stock remain. Click Generate — the AI analyses your order history. Takes 30–60 seconds via Ollama." />
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">AI-predicted weekly demand & stock runway</p>
                </div>
                <button onClick={loadForecast} disabled={aiLoading.forecast}
                  className="text-xs bg-blue-500 text-white px-3 py-1.5 rounded-lg hover:bg-blue-600 disabled:bg-gray-300 flex items-center gap-1">
                  <RefreshCw className={`w-3 h-3 ${aiLoading.forecast ? 'animate-spin' : ''}`} />
                  {aiLoading.forecast ? 'Analyzing...' : 'Generate'}
                </button>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {forecasts.length > 0 ? (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr><Th>Product</Th><Th>Stock</Th><Th>Wkly</Th><Th>Weeks</Th><Th>Reorder</Th><Th>Status</Th></tr>
                    </thead>
                    <tbody className="divide-y">
                      {forecasts.map((f, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <Td className="font-medium text-xs max-w-[120px] truncate">{f.name}</Td>
                          <Td>{f.current_stock}</Td>
                          <Td>{f.weekly_demand}</Td>
                          <Td className={f.weeks_remaining <= 1 ? 'text-red-600 font-bold' : f.weeks_remaining <= 3 ? 'text-amber-600 font-semibold' : ''}>{f.weeks_remaining}</Td>
                          <Td className="font-medium">{f.reorder_qty}</Td>
                          <Td><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${f.urgency === 'critical' ? 'bg-red-100 text-red-700' : f.urgency === 'warning' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>{f.urgency}</span></Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="p-8 text-center text-gray-400">
                    <TrendingUp className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-sm">Click "Generate" to analyse demand patterns</p>
                    <p className="text-xs mt-1 text-gray-300">Uses your Ollama server — takes ~30–60s</p>
                  </div>
                )}
              </div>
            </div>

            {/* Smart Reorder Alerts */}
            <div className="bg-white rounded-xl border shadow-sm">
              <div className="px-5 py-4 border-b flex items-center justify-between bg-gradient-to-r from-orange-50 to-amber-50">
                <div>
                  <h3 className="font-semibold flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4 text-orange-600" />
                    Smart Reorder Alerts
                    <HelpTip text="Tells you exactly which products to order now, how many units, and by when — before you run out. Considers lead times and sales velocity. Click Generate." />
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">AI procurement recommendations</p>
                </div>
                <button onClick={loadReorderAlerts} disabled={aiLoading.reorder}
                  className="text-xs bg-orange-500 text-white px-3 py-1.5 rounded-lg hover:bg-orange-600 disabled:bg-gray-300 flex items-center gap-1">
                  <RefreshCw className={`w-3 h-3 ${aiLoading.reorder ? 'animate-spin' : ''}`} />
                  {aiLoading.reorder ? 'Analyzing...' : 'Generate'}
                </button>
              </div>
              <div className="max-h-96 overflow-y-auto divide-y">
                {reorderAlerts.length > 0 ? reorderAlerts.map((a, i) => (
                  <div key={i} className="px-4 py-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-2">
                        <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${a.alert_type === 'urgent' ? 'bg-red-500' : a.alert_type === 'warning' ? 'bg-amber-500' : 'bg-blue-500'}`}></div>
                        <div>
                          <p className="text-sm font-medium">{a.name || a.sku}</p>
                          <p className="text-xs text-gray-600 mt-0.5">{a.message}</p>
                          <div className="flex gap-3 mt-1.5 text-[10px] text-gray-400">
                            {a.recommended_order_qty && <span>Order: {a.recommended_order_qty} units</span>}
                            {a.estimated_cost && <span>Cost: S${a.estimated_cost}</span>}
                            {a.days_until_stockout != null && <span>Stockout in: {a.days_until_stockout}d</span>}
                          </div>
                        </div>
                      </div>
                      <Badge status={a.alert_type} />
                    </div>
                  </div>
                )) : (
                  <div className="p-8 text-center text-gray-400">
                    <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-sm">Click "Generate" for smart reorder recommendations</p>
                    <p className="text-xs mt-1 text-gray-300">Uses your Ollama server — takes ~30–60s</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── ANALYTICS ── */}
      {aiSubTab === 'analytics' && (
        <div className="space-y-6">
          {/* Dynamic Slotting */}
          <div className="bg-white rounded-xl border shadow-sm">
            <div className="px-5 py-4 border-b bg-gradient-to-r from-teal-50 to-emerald-50 flex items-center justify-between">
              <div>
                <h3 className="font-semibold flex items-center gap-1">
                  <Warehouse className="w-4 h-4 text-teal-600" />
                  Dynamic Slotting
                  <HelpTip text="Suggests optimal warehouse placement. Zone A = fast-selling items near dispatch (quick picking). Zone B = medium. Zone C = slow-moving (back of warehouse). Improves pick efficiency." />
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">AI-suggested warehouse zone placement based on sales velocity</p>
              </div>
              <button onClick={loadSlotting} disabled={slottingLoading}
                className="text-xs bg-teal-500 text-white px-3 py-1.5 rounded-lg hover:bg-teal-600 disabled:bg-gray-300 flex items-center gap-1">
                <RefreshCw className={`w-3 h-3 ${slottingLoading ? 'animate-spin' : ''}`} />
                {slottingLoading ? 'Analyzing...' : 'Run Analysis'}
              </button>
            </div>
            {slottingError && (
              <div className="mx-5 my-4 bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700 flex items-start gap-2">
                <X className="w-3 h-3 mt-0.5 shrink-0" />{slottingError}
              </div>
            )}
            {slottingData ? (
              <div>
                {slottingData.recommendations?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr><Th>Product</Th><Th>Rec. Zone</Th><Th>Daily Sales</Th><Th>Action</Th><Th>Reasoning</Th><Th>Confirm</Th></tr>
                      </thead>
                      <tbody className="divide-y">
                        {slottingData.recommendations.map((r, i) => {
                          const moveState = confirmedMoves[r.product_name];
                          const isMoveAction = r.action === 'Move' || r.action === 'Consolidate';
                          return (
                            <tr key={i} className={`hover:bg-gray-50 ${moveState === 'done' ? 'bg-emerald-50/30' : ''}`}>
                              <Td className="font-medium text-xs max-w-[160px] truncate">{r.product_name}</Td>
                              <Td>
                                <span className={`text-xs px-2.5 py-1 rounded-full font-bold ${ZONE_COLOR[r.recommended_zone] || 'bg-gray-100 text-gray-600'}`}>
                                  Zone {r.recommended_zone}
                                </span>
                              </Td>
                              <Td className="text-gray-600">{r.daily_sales_rate}/day</Td>
                              <Td>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${r.action === 'Move' ? 'bg-amber-100 text-amber-700' : r.action === 'Keep' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                                  {r.action}
                                </span>
                              </Td>
                              <Td className="text-xs text-gray-500 max-w-[180px] truncate">{r.reasoning}</Td>
                              <Td>
                                {moveState === 'done' ? (
                                  <span className="text-xs text-emerald-600 flex items-center gap-1 font-medium">
                                    <CheckCircle className="w-3.5 h-3.5" />Done
                                  </span>
                                ) : moveState === 'loading' ? (
                                  <span className="text-xs text-gray-400 flex items-center gap-1">
                                    <RefreshCw className="w-3 h-3 animate-spin" />Saving...
                                  </span>
                                ) : moveState === 'error' ? (
                                  <span className="text-xs text-red-500">Failed — retry</span>
                                ) : isMoveAction ? (
                                  <button onClick={() => confirmSlottingMove(r)}
                                    className="flex items-center gap-1 text-xs bg-amber-500 text-white px-3 py-1.5 rounded-lg hover:bg-amber-600 font-medium shadow-sm">
                                    <Check className="w-3 h-3" />{r.action} → Zone {r.recommended_zone}
                                  </button>
                                ) : (
                                  <span className="text-xs text-gray-300">—</span>
                                )}
                              </Td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-8 text-center text-gray-400">
                    <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
                    <p className="text-sm">Current slotting looks optimal — no changes needed.</p>
                  </div>
                )}
                <div className="px-5 py-3 bg-teal-50 border-t text-[10px] text-teal-600">
                  Zone A = high-velocity · Zone B = medium · Zone C = slow · Generated {slottingData.generated_at?.split('T')[0]}
                </div>
              </div>
            ) : (
              <div className="p-8 text-center text-gray-400">
                <Warehouse className="w-10 h-10 mx-auto mb-3 text-teal-200" />
                <p className="font-medium text-gray-600">Run Slotting Analysis</p>
                <p className="text-sm mt-1">AI will suggest optimal warehouse placement for each product based on how fast they sell.</p>
              </div>
            )}
          </div>

          {/* Order Anomaly Detection */}
          <div className="bg-white rounded-xl border shadow-sm">
            <div className="px-5 py-4 border-b bg-gradient-to-r from-red-50 to-orange-50 flex items-center justify-between">
              <div>
                <h3 className="font-semibold flex items-center gap-1">
                  <ShieldAlert className="w-4 h-4 text-red-600" />
                  Order Anomaly Detection
                  <HelpTip text="Reviews recent orders for suspicious patterns: unusually large orders, the same customer placing multiple orders, bulk purchases, or cancelled/returned order clusters. Useful for fraud detection." />
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">Flags suspicious orders — bulk buys, price mismatches, duplicate patterns</p>
              </div>
              <button onClick={loadAnomalies} disabled={anomalyLoading}
                className="text-xs bg-red-500 text-white px-3 py-1.5 rounded-lg hover:bg-red-600 disabled:bg-gray-300 flex items-center gap-1">
                <RefreshCw className={`w-3 h-3 ${anomalyLoading ? 'animate-spin' : ''}`} />
                {anomalyLoading ? 'Scanning...' : 'Scan Orders'}
              </button>
            </div>
            {anomalyError && (
              <div className="mx-5 my-4 bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700 flex items-start gap-2">
                <X className="w-3 h-3 mt-0.5 shrink-0" />{anomalyError}
              </div>
            )}
            {anomalyData ? (
              <div>
                <div className="px-5 py-3 border-b bg-gray-50 flex items-center gap-6 text-sm">
                  <span className="text-gray-500">Scanned: <strong>{anomalyData.orders_analyzed} orders</strong></span>
                  <span className="text-gray-500">Avg order: <strong>S${anomalyData.avg_order_value}</strong></span>
                  <span className={`font-medium ${anomalyData.anomalies?.length > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                    {anomalyData.anomalies?.length > 0 ? `⚠ ${anomalyData.anomalies.length} anomalies detected` : '✓ No anomalies found'}
                  </span>
                </div>
                {anomalyData.anomalies?.length > 0 ? (
                  <div className="divide-y">
                    {anomalyData.anomalies.map((a, i) => (
                      <div key={i} className={`px-5 py-4 border-l-4 ${a.risk_level === 'high' ? 'border-l-red-500 bg-red-50/30' : a.risk_level === 'medium' ? 'border-l-amber-500 bg-amber-50/30' : 'border-l-blue-400'}`}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${RISK_COLOR[a.risk_level] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>{a.risk_level?.toUpperCase()} RISK</span>
                              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full capitalize">{a.anomaly_type?.replace(/_/g, ' ')}</span>
                            </div>
                            <p className="text-sm font-medium">Order: <span className="font-mono text-xs">{a.order_id}</span></p>
                            <p className="text-xs text-gray-600 mt-0.5">{a.description}</p>
                          </div>
                          <span className={`text-xs px-3 py-1.5 rounded-lg font-medium shrink-0 ${a.recommended_action === 'Flag' || a.recommended_action === 'Investigate' ? 'bg-red-100 text-red-700' : a.recommended_action === 'Review' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>{a.recommended_action}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <CheckCircle className="w-10 h-10 mx-auto mb-2 text-emerald-400" />
                    <p className="text-sm font-medium text-gray-600">All orders look normal</p>
                  </div>
                )}
                <div className="px-5 py-2 bg-gray-50 border-t text-[10px] text-gray-400">
                  Scanned {anomalyData.orders_analyzed} most recent orders · Generated {anomalyData.generated_at?.split('T')[0]}
                </div>
              </div>
            ) : (
              <div className="p-8 text-center text-gray-400">
                <ShieldAlert className="w-10 h-10 mx-auto mb-3 text-red-200" />
                <p className="font-medium text-gray-600">Scan for Order Anomalies</p>
                <p className="text-sm mt-1">AI reviews recent orders for suspicious patterns, bulk buys, and duplicate customers.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
